// frontend/src/modules/invoicing/pages/RecurringEditorPage.tsx
import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { Button } from '../../../components/ui/button';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { useApi } from '../../../design-system/useApi';
import { useBranding } from '../../../core/branding/BrandingProvider';
import { VISUAL } from '../../../design-system/visual';
import { Trash2 } from 'lucide-react';
import { ClientPicker } from '../components/ClientPicker';
import { InvoiceLineItemsEditor } from '../components/InvoiceLineItemsEditor';
import { recurringApi } from '../api';
import type { Cadence, CreateInvoiceItemInput, CreateRecurringInput, RecurringInvoice } from '../types';

function todayISO() { return new Date().toISOString().slice(0, 10); }

export function RecurringEditorPage() {
  const { id } = useParams<{ id: string }>();
  const mode = id ? 'edit' : 'new';
  const navigate = useNavigate();
  const { tenant } = useBranding();
  const currency = (tenant as any)?.currency ?? 'USD';
  const confirm = useConfirm();

  const { data, loading } = useApi<{ recurring: RecurringInvoice }>(
    mode === 'edit' && id ? `/api/invoicing/recurring/${id}` : null,
  );

  const [form, setForm] = React.useState<CreateRecurringInput & { is_active?: boolean }>({
    name: '',
    client_id: '',
    cadence: 'monthly',
    interval_count: 1,
    next_run_at: todayISO(),
    end_at: null,
    auto_send: true,
    payment_terms_days: tenant?.default_invoice_due_days ?? 14,
    template_items: [{ description: '', quantity: 1, unit_price: 0, tax_rate: tenant ? Number(tenant.default_tax_rate) : 0 }],
    notes_template: '',
  });
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (data?.recurring) {
      const r = data.recurring;
      setForm({
        name: r.name,
        client_id: r.client_id,
        cadence: r.cadence,
        interval_count: r.interval_count,
        next_run_at: r.next_run_at.slice(0, 10),
        end_at: r.end_at?.slice(0, 10) ?? null,
        auto_send: r.auto_send,
        payment_terms_days: r.payment_terms_days,
        template_items: r.template_items as CreateInvoiceItemInput[],
        notes_template: r.notes_template ?? '',
        is_active: r.is_active,
      });
    }
  }, [data]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name?.trim()) { toast.error('Template name required'); return; }
    if (!form.client_id) { toast.error('Pick a client'); return; }
    setSaving(true);
    try {
      if (mode === 'edit' && id) {
        await recurringApi.update(id, form);
        toast.success('Recurring template updated');
      } else {
        await recurringApi.create(form);
        toast.success('Recurring template created');
      }
      navigate('/dashboard/invoices/recurring');
    } catch (err: any) {
      toast.error('Save failed', { description: err.message });
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!id) return;
    if (!(await confirm({ title: `Delete "${data?.recurring?.name}"?`, description: "It won't generate any more invoices.", destructive: true, confirmLabel: 'Delete' }))) return;
    try {
      await recurringApi.remove(id);
      toast.success('Template deleted');
      navigate('/dashboard/invoices/recurring');
    } catch (err: any) {
      toast.error('Delete failed', { description: err.message });
    }
  }

  if (mode === 'edit' && loading) return <LoadingState variant="page" />;

  return (
    <form onSubmit={save}>
      <PageHeader
        eyebrow={<><Link to="/dashboard/invoices/recurring" className="hover:text-foreground transition-colors">Recurring</Link> / {mode === 'edit' ? data?.recurring?.name : 'New'}</>}
        title={mode === 'edit' ? `Edit ${data?.recurring?.name ?? 'recurring template'}` : 'New recurring template'}
        description="A template that generates invoices on a schedule — retainers, subscriptions, anything regular."
        actions={
          <div className="flex items-center gap-2">
            {mode === 'edit' && (
              <Button variant="ghost" type="button" onClick={remove} className="rounded-full h-10 px-3 text-[13px] font-semibold gap-1.5 text-muted-foreground hover:text-destructive">
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </Button>
            )}
            <Link to="/dashboard/invoices/recurring">
              <Button variant="outline" type="button" className="rounded-full h-10 px-4 text-[13px] font-semibold">Cancel</Button>
            </Link>
            <Button type="submit" disabled={saving} className="rounded-full h-10 px-5 text-[13px] font-semibold">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        }
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 max-w-5xl space-y-6">
        <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
          <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>Identity & client</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>Template name<span className="text-destructive ml-0.5">*</span></Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Monthly retainer" required />
            </div>
            <div className="grid gap-1.5">
              <Label>Client<span className="text-destructive ml-0.5">*</span></Label>
              <ClientPicker value={form.client_id || null} onChange={(c) => setForm({ ...form, client_id: c })} />
            </div>
          </div>
        </section>

        <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
          <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>Schedule</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="grid gap-1.5">
              <Label>Cadence</Label>
              <Select value={form.cadence} onValueChange={(v) => setForm({ ...form, cadence: v as Cadence })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Every N</Label>
              <Input type="number" min="1" value={form.interval_count} onChange={(e) => setForm({ ...form, interval_count: Math.max(1, Number(e.target.value)) })} />
            </div>
            <div className="grid gap-1.5">
              <Label>Next run</Label>
              <Input type="date" value={form.next_run_at} onChange={(e) => setForm({ ...form, next_run_at: e.target.value })} />
            </div>
            <div className="grid gap-1.5">
              <Label>End date <span className="text-muted-foreground">(optional)</span></Label>
              <Input type="date" value={form.end_at ?? ''} onChange={(e) => setForm({ ...form, end_at: e.target.value || null })} />
            </div>
          </div>
        </section>

        <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
          <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>Behavior</h3>
          <div className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.auto_send ?? true}
                onChange={(e) => setForm({ ...form, auto_send: e.target.checked })}
                className="mt-1 h-4 w-4 rounded border-border accent-saffron"
              />
              <span className="flex-1">
                <span className="text-[13.5px] font-semibold text-foreground block">Auto-send generated invoices</span>
                <span className="text-[12.5px] text-muted-foreground block mt-0.5">When off, generated invoices are saved as drafts for review.</span>
              </span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label>Payment terms (days)</Label>
                <Input type="number" min="0" max="365" value={form.payment_terms_days ?? 14} onChange={(e) => setForm({ ...form, payment_terms_days: Number(e.target.value) })} />
              </div>
              {mode === 'edit' && (
                <label className="flex items-start gap-3 cursor-pointer self-end pb-2">
                  <input
                    type="checkbox"
                    checked={form.is_active ?? true}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    className="mt-1 h-4 w-4 rounded border-border accent-saffron"
                  />
                  <span className="flex-1">
                    <span className="text-[13.5px] font-semibold text-foreground block">Template is active</span>
                    <span className="text-[12.5px] text-muted-foreground block mt-0.5">Unchecked = paused; no new invoices generated.</span>
                  </span>
                </label>
              )}
            </div>
          </div>
        </section>

        <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
          <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>Line items</h3>
          <InvoiceLineItemsEditor
            items={form.template_items}
            onChange={(items) => setForm({ ...form, template_items: items })}
            currency={currency}
          />
        </section>

        <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
          <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>Notes</h3>
          <div className="grid gap-1.5">
            <Label>Notes <span className="text-muted-foreground font-normal">(copied onto each generated invoice)</span></Label>
            <Textarea rows={3} value={form.notes_template ?? ''} onChange={(e) => setForm({ ...form, notes_template: e.target.value })} placeholder="Thanks for the continued business. Payment instructions, service summary, anything constant across cycles." />
          </div>
        </section>

        <div className="flex items-center justify-between pt-2">
          <Link to="/dashboard/invoices/recurring" className="text-[13px] font-semibold text-muted-foreground hover:text-foreground transition-colors">
            Back without saving
          </Link>
          <Button type="submit" disabled={saving} className="rounded-full h-11 px-6 text-[14px] font-semibold">
            {saving ? 'Saving…' : (mode === 'edit' ? 'Save changes' : 'Create template')}
          </Button>
        </div>
      </div>
    </form>
  );
}
